import { CoursePageFragment } from "@/__generated__/graphql";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import IconLegend, { IconLegendItem } from "@components/IconLegend";
import { slugify } from "@/lib/utils";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import { WindowWidth } from "@lib/hooks/WindowWidth";
import { useMobile } from "@lib/hooks/useMobile";
import { GlobalPageProps } from "@lib/utils";
import { GridContainer, GridWrapper } from "@theme/lib";
import { ReactElement } from "react";
import { JsonLd } from "react-schemaorg";
import { Course } from "schema-dts";

export type CoursePageProps = ComponentProps<
  CoursePageFragment
>;

export default function CoursePage({ id, previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, title, content, hero, informationSection, pageUrl }: CoursePageProps & GlobalPageProps): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero) : null;

  const is1080 = useMobile(1080);
  const is960 = useMobile(960);
  const isMobile = useMobile(WindowWidth.Mobile);

  const iconOrder = [
    'qualification',
    'course-level',
    'duration',
    'uk-fee',
    'international-fee',
    'academic-partners',
    'location',
  ];

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      <JsonLd<Course>
        item={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": heroProps?.title || title,
          "description": metatags?.metaDescription || '',
        }}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl}/>
      }
      {heroProps && <Hero {...heroProps} />}
      <main>
        {informationSection?.tiles?.length &&
          <GridContainer>
            <GridWrapper alignment={"full-width"}>
              <IconLegend
                toggle={isMobile}
                toggleItem="qualification"
                toggleLabel="Course details"
                grid={isMobile ? 1 : is960 ? 3 : is1080 ? 4 : 5}
                items={[...informationSection.tiles]
                  .sort((a, b) => {
                    const aIndex = iconOrder.indexOf(slugify(a.label));
                    const bIndex = iconOrder.indexOf(slugify(b.label));
                    return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
                  })
                  .flatMap(({ label, value }) => [{
                    type: '' as IconLegendItem['type'],
                    label,
                    info: value ?? undefined,
                  }])}
              />
            </GridWrapper>
          </GridContainer>}
        {content && <Slices {...content} />}
      </main>
    </>
  );
}
